import { SitecoreFetch } from 'Api/api/SitecoreApi';
import { HttpStatusCodes } from 'Utilities/ClientResponseUtility';
import { getAntiForgeryToken } from 'Utilities/AntiForgeryToken';

export type AIAnswerType = 'fact' | 'compare' | 'document_lookup' | 'process' | 'insufficient_evidence';

export interface IAIPortalCitation {
  id?: string;
  source?: string;
  title: string;
  tools?: string[];
  url: string;
}

export interface IAIPortalAnswerResponse {
  answer: string;
  answerType: AIAnswerType;
  confidence: number | null;
  citations: IAIPortalCitation[];
}

const ANSWER_ENDPOINT = '/api/sitecore/AIPortal/Answer';

const AI_ANSWER_TYPES: AIAnswerType[] = ['fact', 'compare', 'document_lookup', 'process', 'insufficient_evidence'];

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseCitation(value: unknown): IAIPortalCitation | null {
  if (!isRecord(value) || typeof value.title !== 'string' || typeof value.url !== 'string') {
    return null;
  }

  return {
    id: typeof value.id === 'string' ? value.id : undefined,
    title: value.title,
    url: value.url,
    source: typeof value.source === 'string' ? value.source : undefined,
    tools: Array.isArray(value.tools) ? value.tools.filter((tool): tool is string => typeof tool === 'string') : undefined,
  };
}

export function parseAIAnswerResponse(value: unknown): IAIPortalAnswerResponse | null {
  if (!isRecord(value) || typeof value.answer !== 'string') {
    return null;
  }

  const answerType = AI_ANSWER_TYPES.includes(value.answerType as AIAnswerType)
    ? (value.answerType as AIAnswerType)
    : 'fact';
  const answer = value.answer.trim();
  if (!answer && answerType !== 'insufficient_evidence') {
    return null;
  }
  const confidence =
    typeof value.confidence === 'number' && Number.isFinite(value.confidence) && value.confidence >= 0 && value.confidence <= 1
      ? value.confidence
      : null;
  const citations = Array.isArray(value.citations)
    ? value.citations.map(parseCitation).filter((citation): citation is IAIPortalCitation => citation !== null)
    : [];

  return { answer, answerType, confidence, citations };
}

// The AI Runtime keys its conversation memory on the sessionId it receives (it becomes the LangGraph
// "thread_id"), so this id defines what one conversation is. Scoping it to the browser tab means the
// summary and every follow-up question share a thread, which is what lets the assistant answer
// "what about the second one?" against the summary it just produced. A new tab starts a clean thread.
// It is only half of the value the runtime sees: Sitecore prefixes it with the authenticated user
// server-side, so this id can never be used to reach another user's thread.
const CONVERSATION_ID_STORAGE_KEY = 'ai-summary-conversation-id';

// Kept to characters the runtime's own sessionId contract allows (/^[A-Za-z0-9._:-]+$/, max 128), with
// room left over for the user prefix Sitecore adds. A tampered or truncated stored value is replaced.
const CONVERSATION_ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;

function createConversationId(): string {
  // randomUUID is only exposed in secure contexts; getRandomValues is always available.
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function getConversationId(): string {
  const storedConversationId = sessionStorage.getItem(CONVERSATION_ID_STORAGE_KEY);
  if (storedConversationId && CONVERSATION_ID_PATTERN.test(storedConversationId)) {
    return storedConversationId;
  }

  const conversationId = createConversationId();
  sessionStorage.setItem(CONVERSATION_ID_STORAGE_KEY, conversationId);
  return conversationId;
}

/**
 * Asks the AI Portal a question on this tab's conversation thread.
 *
 * `isSuperseded` is polled either side of each network hop so a caller can abandon work whose result
 * is no longer wanted (a newer search, a reset conversation). When it reports true the promise
 * resolves to null and the caller should render nothing; every other failure throws.
 */
export async function requestAIAnswer(
  query: string,
  isSuperseded: () => boolean = () => false,
): Promise<IAIPortalAnswerResponse | null> {
  const requestVerificationToken = await getAntiForgeryToken();
  if (isSuperseded()) {
    return null;
  }

  if (!requestVerificationToken) {
    throw new Error('Unable to initialize a secure AI request.');
  }

  const response = await SitecoreFetch(ANSWER_ENDPOINT, {
    method: 'POST',
    body: new URLSearchParams({
      __RequestVerificationToken: requestVerificationToken,
      query,
      sessionId: getConversationId(),
      includeFollowUps: 'true',
    }),
  });

  if (isSuperseded()) {
    return null;
  }

  const parsedAnswer = parseAIAnswerResponse(response.Data);
  if (response.Status.Code !== HttpStatusCodes.OK || !parsedAnswer) {
    throw new Error('The AI Portal did not return a usable answer.');
  }

  return parsedAnswer;
}
